<template>
  <ContentWrap title="清洁任务管理">
    <!-- 搜索工作栏 -->
    <el-form :model="queryParams" ref="queryFormRef" :inline="true" label-width="68px" class="-mb-15px">
      <el-form-item label="店铺" prop="shopId">
        <el-select
          v-model="queryParams.shopId"
          placeholder="请选择店铺"
          clearable
          filterable
          class="!w-160px"
          @change="handleQuery"
        >
          <el-option v-for="item in shopList" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="房间" prop="roomId">
        <el-select
          v-model="queryParams.roomId"
          placeholder="请选择房间"
          clearable
          class="!w-180px"
        >
          <el-option
            v-for="r in roomOptions"
            :key="r.id"
            :label="r.roomNo || '#' + r.id"
            :value="r.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="任务类型" prop="taskType">
        <el-select
          v-model="queryParams.taskType"
          placeholder="请选择类型"
          clearable
          class="!w-160px"
        >
          <el-option label="退房清洁" :value="0" />
          <el-option label="日常清洁" :value="1" />
          <el-option label="深度清洁" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item label="任务状态" prop="status">
        <el-select
          v-model="queryParams.status"
          placeholder="请选择状态"
          clearable
          class="!w-160px"
        >
          <el-option label="待分配" :value="0" />
          <el-option label="已分配" :value="1" />
          <el-option label="清洁中" :value="2" />
          <el-option label="待验收" :value="3" />
          <el-option label="已完成" :value="4" />
          <el-option label="已取消" :value="5" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" /> 搜索</el-button>
        <el-button @click="resetQuery"><Icon icon="ep:refresh" class="mr-5px" /> 重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 任务列表 -->
  <ContentWrap>
    <el-table v-loading="loading" :data="list" :stripe="true" border>
      <el-table-column label="房间号" align="center" prop="roomNo" width="120" />
      <el-table-column label="任务类型" align="center" prop="taskType" width="120">
        <template #default="scope">
          <el-tag :type="getTaskTypeTag(scope.row.taskType)">
            {{ getTaskTypeLabel(scope.row.taskType) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" align="center" prop="status" width="100">
        <template #default="scope">
          <el-tag :type="getStatusTag(scope.row.status)">
            {{ getStatusLabel(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="负责人" align="center" prop="assigneeName" width="120">
        <template #default="scope">
          <span>{{ scope.row.assigneeName || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="分配时间" align="center" prop="assignTime" width="160">
        <template #default="scope">
          <span>{{ scope.row.assignTime || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="开始时间" align="center" prop="startTime" width="160">
        <template #default="scope">
          <span>{{ scope.row.startTime || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="完成时间" align="center" prop="completeTime" width="160">
        <template #default="scope">
          <span>{{ scope.row.completeTime || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="验收人" align="center" prop="verifyUser" width="100">
        <template #default="scope">
          <span>{{ scope.row.verifyUser || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="验收时间" align="center" prop="verifyTime" width="160">
        <template #default="scope">
          <span>{{ scope.row.verifyTime || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="备注" align="center" prop="remark" min-width="150">
        <template #default="scope">
          <span>{{ scope.row.remark || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center" width="220" fixed="right">
        <template #default="scope">
          <!-- 状态0: 待分配 → 分配按钮 -->
          <el-button
            v-if="scope.row.status === 0"
            link
            type="primary"
            @click="openAssignDialog(scope.row)"
            v-hasPermi="['hotel:cleaning:update']"
          >
            分配
          </el-button>
          <!-- 状态1: 已分配 → 开始清洁 -->
          <el-button
            v-if="scope.row.status === 1"
            link
            type="success"
            @click="handleStart(scope.row.id)"
            v-hasPermi="['hotel:cleaning:update']"
          >
            开始清洁
          </el-button>
          <!-- 状态2: 清洁中 → 完成清洁 -->
          <el-button
            v-if="scope.row.status === 2"
            link
            type="warning"
            @click="handleComplete(scope.row.id)"
            v-hasPermi="['hotel:cleaning:update']"
          >
            完成清洁
          </el-button>
          <!-- 状态3: 待验收 → 验收 -->
          <el-button
            v-if="scope.row.status === 3"
            link
            type="success"
            @click="openVerifyDialog(scope.row)"
            v-hasPermi="['hotel:cleaning:verify']"
          >
            验收
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      :total="total"
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 分配弹窗 -->
  <Dialog :title="'分配清洁任务'" v-model="assignDialogVisible" width="450px">
    <el-form ref="assignFormRef" :model="assignFormData" :rules="assignFormRules" label-width="100px" v-loading="assignFormLoading">
      <el-form-item label="指派人" prop="assigneeId">
        <el-select v-model="assignFormData.assigneeId" placeholder="请选择指派人" filterable @change="onAssigneeChange">
          <el-option v-for="item in employeeList" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="submitAssign" type="primary" :disabled="assignFormLoading">确 定</el-button>
      <el-button @click="assignDialogVisible = false">取 消</el-button>
    </template>
  </Dialog>

  <!-- 验收弹窗 -->
  <Dialog :title="'验收清洁任务'" v-model="verifyDialogVisible" width="450px">
    <el-form ref="verifyFormRef" :model="verifyFormData" :rules="verifyFormRules" label-width="100px" v-loading="verifyFormLoading">
      <el-form-item label="验收人" prop="verifyUser">
        <el-select v-model="verifyFormData.verifyUser" placeholder="请选择验收人" filterable>
          <el-option v-for="item in employeeList" :key="item.id" :label="item.name" :value="item.name" />
        </el-select>
      </el-form-item>
      <el-form-item label="验收备注" prop="verifyRemark">
        <el-input v-model="verifyFormData.verifyRemark" type="textarea" placeholder="请输入验收备注" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="submitVerify" type="primary" :disabled="verifyFormLoading">确 定</el-button>
      <el-button @click="verifyDialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import * as cleaningApi from '@/api/hotel/cleaning'
import * as roomApi from '@/api/hotel/room'
import { getEmployeeListAll } from '@/api/hotel/employee'
import { getShopSimpleList } from '@/api/hotel/platformShop'

defineOptions({ name: 'HotelCleaning' })

const message = useMessage()

const loading = ref(true)
const list = ref<cleaningApi.CleaningVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  shopId: undefined,
  roomId: undefined,
  taskType: undefined,
  status: undefined
})
const queryFormRef = ref()

// 店铺下拉列表
const shopList = ref<any[]>([])
const getShopList = async () => {
  const data = await getShopSimpleList()
  shopList.value = data || []
}

// 员工下拉列表
const employeeList = ref<any[]>([])
const getEmployeeList = async () => {
  const data = await getEmployeeListAll()
  employeeList.value = data || []
}

const taskTypeMap: Record<number, string> = { 0: '退房清洁', 1: '日常清洁', 2: '深度清洁' }
const taskTypeTagMap: Record<number, string> = { 0: 'danger', 1: 'primary', 2: 'warning' }
const getTaskTypeLabel = (type: number) => taskTypeMap[type] || '未知'
const getTaskTypeTag = (type: number) => taskTypeTagMap[type] || 'info'

const statusMap: Record<number, string> = { 0: '待分配', 1: '已分配', 2: '清洁中', 3: '待验收', 4: '已完成', 5: '已取消' }
const statusTagMap: Record<number, string> = { 0: 'info', 1: 'warning', 2: 'primary', 3: '', 4: 'success', 5: 'info' }
const getStatusLabel = (status: number) => statusMap[status] || '未知'
const getStatusTag = (status: number) => statusTagMap[status] || 'info'

const getList = async () => {
  loading.value = true
  try {
    const data = await cleaningApi.getCleaningPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

// 分配
const assignDialogVisible = ref(false)
const assignFormLoading = ref(false)
const assignFormRef = ref()
const currentTaskId = ref<string | number>()
const assignFormData = reactive({
  assigneeId: undefined as string | number | undefined,
  assigneeName: ''
})
const assignFormRules = reactive({
  assigneeId: [{ required: true, message: '请选择指派人', trigger: 'change' }]
})

// 选择指派人时联动赋值 assigneeName
const onAssigneeChange = (val: any) => {
  const emp = employeeList.value.find((e: any) => e.id === val)
  assignFormData.assigneeName = emp ? emp.name : ''
}

const openAssignDialog = (row: cleaningApi.CleaningVO) => {
  currentTaskId.value = row.id
  assignFormData.assigneeId = undefined
  assignFormData.assigneeName = ''
  assignFormRef.value?.resetFields()
  assignDialogVisible.value = true
}

const submitAssign = async () => {
  await assignFormRef.value.validate()
  assignFormLoading.value = true
  try {
    await cleaningApi.assign({
      id: currentTaskId.value!,
      assigneeId: assignFormData.assigneeId!,
      assigneeName: assignFormData.assigneeName
    })
    message.success('分配成功')
    assignDialogVisible.value = false
    await getList()
  } finally {
    assignFormLoading.value = false
  }
}

// 开始清洁
const handleStart = async (id: string | number) => {
  try {
    await message.confirm('确认开始清洁？')
    await cleaningApi.start(id)
    message.success('已开始清洁')
    await getList()
  } catch {}
}

// 完成清洁
const handleComplete = async (id: string | number) => {
  try {
    await message.confirm('确认完成清洁？')
    await cleaningApi.complete(id)
    message.success('清洁完成')
    await getList()
  } catch {}
}

// 验收
const verifyDialogVisible = ref(false)
const verifyFormLoading = ref(false)
const verifyFormRef = ref()
const verifyFormData = reactive({
  verifyUser: '',
  verifyRemark: ''
})
const verifyFormRules = reactive({
  verifyUser: [{ required: true, message: '请选择验收人', trigger: 'change' }]
})

const openVerifyDialog = (row: cleaningApi.CleaningVO) => {
  currentTaskId.value = row.id
  verifyFormData.verifyUser = ''
  verifyFormData.verifyRemark = ''
  verifyFormRef.value?.resetFields()
  verifyDialogVisible.value = true
}

const submitVerify = async () => {
  await verifyFormRef.value.validate()
  verifyFormLoading.value = true
  try {
    await cleaningApi.verify({
      id: currentTaskId.value!,
      verifyUser: verifyFormData.verifyUser,
      verifyRemark: verifyFormData.verifyRemark
    })
    message.success('验收成功')
    verifyDialogVisible.value = false
    await getList()
  } finally {
    verifyFormLoading.value = false
  }
}

// 房间下拉选项
const roomOptions = ref<{ id: string | number; roomNo: string }[]>([])
const loadRoomOptions = async () => {
  const data = await roomApi.listAllRooms()
  roomOptions.value = data.list || []
}

onMounted(() => {
  getList()
  loadRoomOptions()
  getEmployeeList()
  getShopList()
})
</script>
